import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Map, LayoutGrid, Loader2, ShieldAlert } from 'lucide-react';
import { useModuleSettings, MODULE_DEFINITIONS } from '@/hooks/useModuleSettings';
import { useMapProvider } from '@/hooks/useMapProvider';
import { NotificationTemplateEditor } from '@/components/notifications/NotificationTemplateEditor';
import { CompanySettingsForm } from '@/components/crm/CompanySettingsForm';

export function SystemSettingsTab() {
  const { isLoading: modulesLoading, isModuleEnabled, toggleModule, isUpdating } = useModuleSettings();
  const { mapProvider, setMapProvider, isAdmin: isMapAdmin, isUpdating: isMapUpdating } = useMapProvider();

  return (
    <div className="grid gap-4 sm:gap-6">
      {/* Admin Notice */}
      <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800">
        <ShieldAlert className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
        <p className="text-sm text-amber-800 dark:text-amber-200">
          Deze instellingen zijn alleen zichtbaar voor administrators en beïnvloeden alle gebruikers.
        </p>
      </div>

      {/* Company Settings */}
      <CompanySettingsForm />

      {/* Map Provider */}
      {isMapAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Map className="h-5 w-5" />
              Kaart Provider
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Kies welke kaartprovider wordt gebruikt voor alle kaarten in de applicatie.
            </p>
            
            <div className="flex gap-2">
              <Button
                variant={mapProvider === 'mapbox' ? 'default' : 'outline'}
                onClick={() => setMapProvider('mapbox')}
                disabled={isMapUpdating}
                className="flex-1"
              >
                Mapbox
              </Button>
              <Button
                variant={mapProvider === 'leaflet' ? 'default' : 'outline'}
                onClick={() => setMapProvider('leaflet')}
                disabled={isMapUpdating}
                className="flex-1"
              >
                Leaflet
              </Button>
            </div>

            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800">
              <p className="text-xs text-amber-800 dark:text-amber-200">
                <strong>Let op:</strong> Wijzigingen zijn direct zichtbaar voor alle gebruikers.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Module Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LayoutGrid className="h-5 w-5" />
            Module Beheer
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Schakel modules in of uit voor alle gebruikers. Uitgeschakelde modules verdwijnen uit het menu.
          </p>
          
          {modulesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4">
              {MODULE_DEFINITIONS.map(module => (
                <div key={module.key} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex-1 min-w-0">
                    <Label className="font-medium">{module.label}</Label>
                    <p className="text-xs text-muted-foreground truncate">{module.description}</p>
                  </div>
                  <Switch
                    checked={isModuleEnabled(module.key)}
                    onCheckedChange={(checked) => toggleModule(module.key, checked)}
                    disabled={isUpdating}
                  />
                </div>
              ))}
            </div>
          )}
          
          <div className="mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800">
            <p className="text-xs text-amber-800 dark:text-amber-200">
              <strong>Let op:</strong> Wijzigingen zijn direct zichtbaar voor alle gebruikers na het herladen van de pagina.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Notification Template Editor */}
      <NotificationTemplateEditor />
    </div>
  );
}
